"use client"

import { useEffect } from "react"

/**
 * Behaviour of the homepage, ported from the prototype's script (media/web/homepage/homepage-src.html).
 *
 * The markup is server-rendered and finished: without this component the page stands as the end state.
 * `html.mo` (motion allowed) is set by the inline script in home-page.tsx before first paint; the start
 * states in home.css apply only under it. Everything here is a change of state at the reading line.
 * Scroll work is batched into one requestAnimationFrame per frame and reads no layout except the sticky
 * case index: the static positions (document coordinates) are cached by measure(), which runs on mount, on a
 * size change of the page, the header or the index (ResizeObserver), and on load, font load and pageshow.
 */

const BEAT = 120
const SIGC = "cubic-bezier(.65,0,.15,1)"
const STDC = "cubic-bezier(.2,0,.38,.9)"
const RM_KEY = "aient-rm"

type Rgb = [number, number, number]
type Stroke = [number, number, number, number] // centre x, centre y, length, angle

const at = <T,>(a: readonly T[], i: number): T => a[i] as T

function bez(x1: number, y1: number, x2: number, y2: number) {
  return (t: number) => {
    if (t <= 0) return 0
    if (t >= 1) return 1
    let lo = 0
    let hi = 1
    let m = 0.5
    for (let n = 0; n < 30; n++) {
      m = (lo + hi) / 2
      const x = 3 * (1 - m) * (1 - m) * m * x1 + 3 * (1 - m) * m * m * x2 + m * m * m
      if (x < t) lo = m
      else hi = m
    }
    m = (lo + hi) / 2
    return 3 * (1 - m) * (1 - m) * m * y1 + 3 * (1 - m) * m * m * y2 + m * m * m
  }
}
const SIGN = bez(0.65, 0, 0.15, 1)
const STD = bez(0.2, 0, 0.38, 0.9)

/** Stage colours: field, gutter shade, as in the script's ST and GS tables of the prototype. */
const COL: Rgb[] = [
  [76, 38, 28],
  [48, 60, 55],
  [35, 63, 44],
  [211, 237, 124],
]
const GS: Rgb[] = [
  [62, 31, 23],
  [39, 49, 45],
  [29, 52, 36],
  [195, 223, 99],
]
const PAPER: Rgb = [245, 243, 233]
const INK: Rgb = [21, 47, 39]
/** The stage symbol as two strokes: − = + ✓ */
const SHP: Array<[Stroke, Stroke]> = [
  [
    [9, 9, 10.5, 0],
    [9, 9, 10.5, 0],
  ],
  [
    [9, 6.75, 10.5, 0],
    [9, 11.25, 10.5, 0],
  ],
  [
    [9, 9, 10.5, 0],
    [9, 9, 10.5, 90],
  ],
  [
    [5.05, 11.45, 5.8, 45],
    [11.05, 9.2, 11.68, 132.57],
  ],
]

const lerp = <T extends number[]>(a: T, b: readonly number[], t: number) => a.map((v, i) => v + ((b[i] ?? 0) - v) * t) as T
const rgb = (c: readonly number[]) => `rgb(${c.map(Math.round).join(",")})`

function put(l: SVGLineElement, p: Stroke) {
  const a = (p[3] * Math.PI) / 180
  const dx = (Math.cos(a) * p[2]) / 2
  const dy = (Math.sin(a) * p[2]) / 2
  l.setAttribute("x1", String(p[0] - dx))
  l.setAttribute("y1", String(p[1] - dy))
  l.setAttribute("x2", String(p[0] + dx))
  l.setAttribute("y2", String(p[1] + dy))
}

export function HomeBehaviour() {
  useEffect(() => {
    const hp = document.querySelector<HTMLElement>(".hp")
    if (!hp) return
    const root = document.documentElement
    const off: Array<() => void> = []
    const on = <T extends EventTarget>(t: T, type: string, fn: EventListener, opts?: AddEventListenerOptions) => {
      t.addEventListener(type, fn, opts)
      off.push(() => t.removeEventListener(type, fn, opts))
    }
    const q = <T extends Element>(s: string) => hp.querySelector<T>(s)
    const qa = <T extends Element>(s: string) => Array.from(hp.querySelectorAll<T>(s))
    const timers: number[] = []
    const later = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms))
    }

    ;(window as unknown as { aientReady?: boolean }).aientReady = true
    const RED = () => root.classList.contains("rm")

    /* Reduce motion: the footer toggle overrides the system in either direction, remembered on this device */
    const toggles = qa<HTMLButtonElement>("[data-rm]")
    const mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null
    let chosen: string | null = null
    try {
      chosen = localStorage.getItem(RM_KEY)
    } catch (error) {
      void error // storage unavailable (private mode): the page works without the saved choice
    }
    const setRM = (reduce: boolean, save: boolean) => {
      root.classList.toggle("rm", reduce)
      root.classList.toggle("mo", !reduce)
      toggles.forEach((b) => b.setAttribute("aria-pressed", reduce ? "true" : "false"))
      if (save) {
        try {
          localStorage.setItem(RM_KEY, reduce ? "1" : "0")
        } catch (error) {
      void error // storage unavailable (private mode): the page works without the saved choice
    }
      }
      if (reduce) {
        document.getAnimations?.().forEach((a) => a.finish())
        qa(".dz,.mcp,.sbx,.chat").forEach((d) => d.classList.add("done"))
      }
    }
    setRM(RED(), false)
    toggles.forEach((b) =>
      on(b, "click", () => {
        chosen = RED() ? "0" : "1"
        setRM(!RED(), true)
      }),
    )
    if (mq) {
      const change = () => {
        if (chosen === null) setRM(mq.matches, false)
      }
      mq.addEventListener("change", change)
      off.push(() => mq.removeEventListener("change", change))
    }

    /* Header: hides on scroll down past 8u, returns on scroll up; never while focus or the menu is inside.
       Inside the case the index is the only bar, so the snap point never moves under the reader. */
    const hdr = q<HTMLElement>("#hdr")
    const mp = q<HTMLElement>("#mpanel")
    const caseEl = q<HTMLElement>(".case")
    const firstStep = q<HTMLElement>(".step")
    const cbtn = q<HTMLElement>(".c-btn")
    const idxEl = q<HTMLElement>(".c-idx")
    const steps = caseEl ? Array.from(caseEl.querySelectorAll<HTMLElement>(".step")) : []
    const secs = qa<HTMLElement>(".sec")

    /* Cached positions: top and bottom in document coordinates, so a scroll frame needs only scrollY. */
    type Span = { top: number; bottom: number }
    const span = (el: Element | null | undefined, y: number): Span => {
      if (!el) return { top: 0, bottom: 0 }
      const r = el.getBoundingClientRect()
      return { top: r.top + y, bottom: r.bottom + y }
    }
    const pos = {
      hh: 0,
      idxh: 0,
      docH: 0,
      firstStep: 0,
      caseBottom: 0,
      steps: [] as number[],
      titles: [] as number[],
      secBottoms: [] as number[],
      late: [] as Span[],
      pics: [] as number[],
    }
    const measure = () => {
      const y = window.scrollY
      pos.hh = hdr?.offsetHeight ?? 0
      pos.idxh = idxEl?.offsetHeight ?? 0
      pos.docH = document.documentElement.scrollHeight
      pos.firstStep = firstStep ? firstStep.getBoundingClientRect().top + y : 0
      pos.caseBottom = caseEl ? caseEl.getBoundingClientRect().bottom + y : 0
      /* a missing element never reaches the reading line */
      const topOf = (el: Element | null | undefined) => (el ? span(el, y).top : Number.POSITIVE_INFINITY)
      pos.steps = steps.map(topOf)
      pos.titles = secs.map((s) => topOf(s.querySelector(".title")))
      /* a section's bottom: 0 keeps a section without one from ever holding the line */
      pos.secBottoms = secs.map((s) => span(s, y).bottom)
      pos.late = late.map((d) => span(d, y))
      pos.pics = pics.map(([el]) => topOf(el))
    }

    let lastY = window.scrollY
    let lastHdr = ""
    let lastPad = ""
    const hdrState = (y: number, vh: number) => {
      if (!hdr || !mp) return
      const keep = hdr.contains(document.activeElement) || !mp.hidden
      const inCase = Boolean(
        caseEl &&
          firstStep &&
          pos.firstStep - y < vh * 0.5 &&
          pos.caseBottom - y > vh * 0.5 &&
          !window.matchMedia("(max-height:600px)").matches,
      )
      const away = !keep && (inCase || (y > 72 && y > lastY))
      if (inCase || Math.abs(y - lastY) > 4 || y < 72) {
        hdr.classList.toggle("away", away)
        lastY = y
      }
      const hh = pos.hh
      const shown = !hdr.classList.contains("away")
      const h = shown ? `${hh}px` : "0px"
      if (h !== lastHdr) {
        hp.style.setProperty("--hdr", h)
        lastHdr = h
      }
      if (cbtn) cbtn.classList.toggle("on", inCase)
      /* keyboard focus never lands under what is pinned */
      const pad = `${(shown ? hh : 0) + (inCase && idxEl ? pos.idxh : 0) + 9}px`
      if (pad !== lastPad) {
        root.style.scrollPaddingTop = pad
        lastPad = pad
      }
    }
    off.push(() => {
      root.style.scrollPaddingTop = ""
    })

    /* The diffs: the strike draws, then the added row rises from under the removed row one beat later,
       then the hero's ticks draw one beat apart. */
    const playDiff = (el: HTMLElement) => {
      el.getAnimations?.({ subtree: true }).forEach((a) => a.cancel())
      el.classList.add("done")
      if (RED() || typeof el.animate !== "function") return
      const T = BEAT
      el.querySelector(".k")?.animate(
        { transform: ["scaleX(0)", "scaleX(1)"] },
        { duration: 3 * T, easing: STDC, fill: "backwards" },
      )
      el.querySelectorAll(".row.add .slide").forEach((x) =>
        x.animate(
          { transform: ["translateY(-100%)", "translateY(0)"] },
          { duration: 3 * T, delay: T, easing: SIGC, fill: "backwards" },
        ),
      )
      el.querySelectorAll(".checks .mk svg").forEach((t, k) =>
        t.animate(
          { strokeDashoffset: [19, 0] },
          { duration: 2 * T, delay: (4 + k) * T, easing: STDC, fill: "backwards" },
        ),
      )
    }
    const hero = q<HTMLElement>("#hero-diff")
    const agon = q<HTMLElement>("#agon-diff")
    const endDiff = q<HTMLElement>("#end-diff")
    let started = false
    const startHero = () => {
      if (started || !hero) return
      started = true
      if (window.scrollY > 9) {
        hero.classList.add("done")
        return
      }
      later(() => playDiff(hero), 2 * BEAT)
    }
    /* Start when Literata has loaded or after 5t, whichever comes first */
    if (document.fonts?.load) document.fonts.load('400 112px "Literata"').then(startHero, startHero)
    later(startHero, 5 * BEAT)

    /* Agon and the end diff play when their top reaches the reading line; a diff plays again only after
       it has been off the screen for 10 s. */
    const late = [agon, endDiff].filter((d): d is HTMLElement => Boolean(d))
    const away = new Map<Element, number>()
    const awayIO =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver((es) =>
            es.forEach((e) => {
              if (!e.isIntersecting) away.set(e.target, Date.now())
            }),
          )
    late.forEach((d) => awayIO?.observe(d))
    off.push(() => awayIO?.disconnect())
    const diffState = (y: number, vh: number, line: number) =>
      late.forEach((d, k) => {
        const r = pos.late[k] ?? { top: 0, bottom: 0 }
        const top = r.top - y
        const bottom = r.bottom - y
        const t = away.get(d)
        if (top <= line && !d.classList.contains("done")) playDiff(d)
        else if (
          d.classList.contains("done") &&
          t &&
          Date.now() - t > 10000 &&
          (top > vh || bottom < 0) &&
          !RED()
        ) {
          d.getAnimations?.({ subtree: true }).forEach((x) => x.cancel())
          d.classList.remove("done")
          away.delete(d)
        }
      })

    /* The case: the stage symbol (strokes as centre, length, angle) morphs in two beats, the ground follows */
    const readLine = (top: number, vh: number) => top + (vh - top) * 0.4
    let caseState = (_y: number, _vh: number, _idxBottom: number) => {}
    if (caseEl && idxEl) {
      const las = Array.from(caseEl.querySelectorAll<SVGLineElement>(".la"))
      const lbs = Array.from(caseEl.querySelectorAll<SVGLineElement>(".lb"))
      const nums = Array.from(caseEl.querySelectorAll<HTMLAnchorElement>(".c-idx a"))
      const pose = (i: number) => at(SHP, i)
      const st = {
        i: 0,
        p: [[...pose(0)[0]], [...pose(0)[1]]] as [Stroke, Stroke],
        c: [...at(COL, 0)] as Rgb,
        g: [...at(GS, 0)] as Rgb,
        k: [...PAPER] as Rgb,
        raf: 0,
      }
      const inkOf = (i: number): Rgb => (i === 3 ? INK : PAPER)
      const draw = () => {
        las.forEach((l) => {
          put(l, st.p[0])
          l.style.stroke = rgb(st.k)
        })
        lbs.forEach((l) => {
          put(l, st.p[1])
          l.style.stroke = rgb(st.k)
        })
        caseEl.style.setProperty("--bg", rgb(st.c))
        caseEl.style.setProperty("--gs", rgb(st.g))
        caseEl.classList.toggle("lt", (0.299 * st.c[0] + 0.587 * st.c[1] + 0.114 * st.c[2]) / 255 > 0.45)
      }
      const go = (i: number) => {
        if (i === st.i) return
        cancelAnimationFrame(st.raf)
        const from = st.i
        const back = i < from
        st.i = i
        nums.forEach((x, j) => {
          if (j === i) x.setAttribute("aria-current", "step")
          else x.removeAttribute("aria-current")
        })
        const target = pose(i)
        /* reduced motion, or a jump of more than one step: the sign and the ground change at once */
        if (RED() || Math.abs(i - from) > 1) {
          st.p = [[...target[0]], [...target[1]]]
          st.c = [...at(COL, i)] as Rgb
          st.g = [...at(GS, i)] as Rgb
          st.k = [...inkOf(i)] as Rgb
          draw()
          return
        }
        const p0: [Stroke, Stroke] = [[...st.p[0]], [...st.p[1]]]
        const c0 = [...st.c] as Rgb
        const g0 = [...st.g] as Rgb
        const k0 = [...st.k] as Rgb
        const t0 = performance.now()
        const mv = 3 * BEAT
        const d = back ? [BEAT, 0] : [0, BEAT]
        const frame = (now: number) => {
          const e = now - t0
          for (const j of [0, 1] as const) st.p[j] = lerp(p0[j], target[j], SIGN((e - (d[j] ?? 0)) / mv))
          /* the ground follows the first stroke: it starts on 1t, once the stroke has committed */
          const f = STD((e - BEAT) / (3 * BEAT))
          st.c = lerp(c0, at(COL, i), f)
          st.g = lerp(g0, at(GS, i), f)
          st.k = lerp(k0, inkOf(i), f)
          draw()
          st.raf = e < 4 * BEAT ? requestAnimationFrame(frame) : 0
        }
        frame(t0)
      }
      let jumping = false
      /* idxBottom is the one live read of a frame: the index is sticky, so its position is not a constant. */
      caseState = (y: number, vh: number, idxBottom: number) => {
        if (jumping) return
        const line = readLine(idxBottom, vh)
        let i = 0
        pos.steps.forEach((top, j) => {
          if (top - y <= line + (j <= st.i ? 27 : -27)) i = j
        })
        go(i)
      }
      nums.forEach((x, j) =>
        on(x, "click", () => {
          jumping = true
          go(j)
          later(() => {
            jumping = false
            steps[j]?.querySelector<HTMLElement>("h3")?.focus({ preventScroll: true })
          }, 700)
        }),
      )
      nums[0]?.setAttribute("aria-current", "step")
      draw()
      off.push(() => cancelAnimationFrame(st.raf))
    }

    /* The current-line marker: beside the title of the white section at the reading line */
    let curSec: HTMLElement | null = null
    const lineState = (y: number, vh: number) => {
      const line = vh * 0.4
      let hit: HTMLElement | null = null
      secs.forEach((s, k) => {
        if ((pos.titles[k] ?? Number.POSITIVE_INFINITY) - y <= line && (pos.secBottoms[k] ?? 0) - y > line) hit = s
      })
      /* a short page header (a blog post's) can end above the line: its title stays current while it is on screen */
      const pk = secs.findIndex((s) => s.classList.contains("pgh"))
      const pghTop = pk >= 0 ? (pos.titles[pk] ?? Number.POSITIVE_INFINITY) - y : Number.POSITIVE_INFINITY
      if (!hit && pk >= 0 && pghTop >= 0 && pghTop <= line) hit = secs[pk] ?? null
      if (vh + y >= pos.docH - 2) hit = secs[secs.length - 1] ?? null
      if (hit !== curSec) {
        curSec?.classList.remove("cur")
        hit?.classList.add("cur")
        curSec = hit
      }
    }

    /* For agents and the thread: the exchange appears line by line; ten runs glide from the laptop into the
       sandboxes one beat apart (CSS transitions); the six messages arrive one beat apart (CSS). */
    const playMcp = (el: HTMLElement) => {
      el.classList.add("done")
      if (RED() || typeof el.animate !== "function") return
      el.querySelectorAll("p").forEach((p, k) =>
        p.animate({ opacity: [0, 1] }, { duration: 2 * BEAT, delay: BEAT + k * 3 * BEAT, easing: STDC, fill: "backwards" }),
      )
    }
    const pics: Array<[HTMLElement | null, (el: HTMLElement) => void]> = [
      [q<HTMLElement>(".mcp"), playMcp],
      [q<HTMLElement>(".sbx"), (el) => el.classList.add("done")],
      [q<HTMLElement>(".chat"), (el) => el.classList.add("done")],
    ]
    const picState = (y: number, line: number) =>
      pics.forEach(([el, play], k) => {
        if (el && !el.classList.contains("done") && (pos.pics[k] ?? Number.POSITIVE_INFINITY) - y <= line) play(el)
      })

    let tick = false
    const onScroll = () => {
      if (tick) return
      tick = true
      requestAnimationFrame(() => {
        tick = false
        const y = window.scrollY
        const vh = window.innerHeight
        const idxBottom = idxEl ? idxEl.getBoundingClientRect().bottom : 0
        const line = readLine(0, vh)
        hdrState(y, vh)
        caseState(y, vh, idxBottom)
        lineState(y, vh)
        diffState(y, vh, line)
        picState(y, line)
      })
    }
    on(window, "scroll", onScroll, { passive: true })
    /* positions change only when the page, the header or the index change size (fonts, images, expanders, viewport) */
    const remeasure = () => {
      measure()
      onScroll()
    }
    /* a viewport resize can reflow sections without changing the observed boxes, so it always remeasures */
    on(window, "resize", remeasure)
    measure()
    onScroll()
    if (typeof ResizeObserver !== "undefined") {
      /* an observer reports every box once when it starts; the measure above already covers that pass */
      let first = true
      const ro = new ResizeObserver(() => {
        if (first) {
          first = false
          return
        }
        remeasure()
      })
      ;[hp, hdr, idxEl].forEach((el) => el && ro.observe(el))
      off.push(() => ro.disconnect())
    }
    document.fonts?.ready.then(remeasure)
    /* a restored scroll position arrives after the script: check again once the page has loaded */
    on(window, "load", () => later(remeasure, 50))
    on(window, "pageshow", remeasure)

    /* Expanders open in place; the closed text is inert */
    qa<HTMLButtonElement>(".xp[aria-controls]").forEach((b) => {
      const panel = document.getElementById(b.getAttribute("aria-controls") ?? "")
      const label = b.firstChild
      if (!panel || !label) return
      const closed = label.textContent ?? ""
      on(b, "click", () => {
        const open = b.getAttribute("aria-expanded") !== "true"
        b.setAttribute("aria-expanded", String(open))
        panel.classList.toggle("open", open)
        panel.inert = !open
        label.textContent = open ? "Hide the full quote " : closed
      })
    })

    /* The film opens in a dialog: focus moves in, Esc closes, focus returns to the opener. Safari does not focus
       a clicked button, so the native <dialog> would return focus to <body>: the opener is focused explicitly. */
    const film = q<HTMLDialogElement>("#film")
    if (film) {
      let opener: HTMLElement | null = null
      qa<HTMLElement>("[data-film]").forEach((b) =>
        on(b, "click", () => {
          opener = b
          film.showModal()
        }),
      )
      film.querySelector("[data-close]")?.addEventListener("click", () => film.close())
      /* Esc, the Close button or a click on the backdrop: stop the film so it never plays on unseen */
      on(film, "close", () => {
        film.querySelector("video")?.pause()
        opener?.focus()
        opener = null
      })
      on(film, "click", (e) => {
        if (e.target === film) film.close()
      })
    }

    /* Phone menu: a modal dialog, Esc closes; everything else in the frame (the skip link too) is inert,
       Tab cycles inside the panel and the page behind does not scroll */
    const mb = hdr?.querySelector<HTMLButtonElement>(".menu")
    const menu = (open: boolean) => {
      if (!mp || !mb) return
      mp.hidden = !open
      mb.setAttribute("aria-expanded", String(open))
      Array.from(hp.children).forEach((x) => {
        if (x !== mp && x instanceof HTMLElement) x.inert = open
      })
      root.style.overflow = open ? "hidden" : ""
      if (open) mp.querySelector<HTMLElement>("[data-close]")?.focus()
      else mb.focus()
    }
    off.push(() => {
      root.style.overflow = ""
    })
    if (mp && mb) {
      on(mb, "click", () => menu(true))
      on(mp, "click", (e) => {
        const t = e.target as Element
        if (t.closest("[data-close]") || t.closest("a")) menu(false)
      })
      on(window, "keydown", (e) => {
        const k = e as KeyboardEvent
        if (mp.hidden) return
        if (k.key === "Escape") menu(false)
        if (k.key !== "Tab") return
        const items = Array.from(mp.querySelectorAll<HTMLElement>("a[href],button"))
        const first = items[0]
        const last = items[items.length - 1]
        const active = document.activeElement
        const outside = !mp.contains(active)
        if (k.shiftKey && (active === first || outside)) {
          k.preventDefault()
          last?.focus()
        } else if (!k.shiftKey && (active === last || outside)) {
          k.preventDefault()
          first?.focus()
        }
      })
    }

    return () => {
      off.forEach((f) => f())
      timers.forEach((t) => clearTimeout(t))
    }
  }, [])

  return null
}
